/**
 * PhoneInput — the shared phone-number field for the whole portal.
 *
 * Ivrello stores phone numbers in E.164, but nobody thinks in E.164. This
 * control renders as ONE joined field: a country-prefix button (pre-filled
 * from the tenant's resolved default — customer override → reseller override
 * → platform setting → '+1') and a national-number input beside it. The
 * caller keeps seeing a single string: `onChange` always emits the COMBINED
 * value (`prefix + national digits`), so every existing form state, zod
 * schema, and strict E.164 regex keeps working untouched.
 *
 * Behavior notes:
 *  - Clicking the prefix opens a searchable country list (name / ISO / code).
 *  - Typed formatting characters are dropped; the national box holds digits.
 *  - A national trunk '0' is dropped on emit (matches the backend
 *    normalizer in backend/src/utils/phone.ts), and "1 555 123 4567" under
 *    a +1 prefix is recognized as already carrying the country code.
 *  - Pasting a full "+…" number into the national box re-parses the prefix.
 *  - Incoming values that already start with '+' are split against the
 *    country table (longest match) for display.
 *  - The whole control is forced LTR (phone numbers read left-to-right even
 *    on RTL pages) and uses logical spacing so it sits naturally in RTL
 *    layouts. Heights follow the neighbouring <Input> via `self-stretch`,
 *    so `inputClassName="h-8 text-sm"` variants Just Work.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { phoneDefaults } from '@/api/phoneDefaults';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/cn';
import { Check, ChevronDown } from 'lucide-react';

// ── Country table ────────────────────────────────────────────────────────────

export interface CountryCode {
  name: string;
  iso: string;
  prefix: string;
}

/** ISO 3166-1 alpha-2 → regional-indicator flag emoji. */
function flagOf(iso: string): string {
  return iso.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

/** The platform's audience first, then a broad alphabetical sweep. */
const COMMON_ISO = new Set(['US', 'CA', 'GB', 'IL', 'MX']);

export const COUNTRY_CODES: CountryCode[] = [
  { name: 'United States',        iso: 'US', prefix: '+1'   },
  { name: 'Canada',               iso: 'CA', prefix: '+1'   },
  { name: 'United Kingdom',       iso: 'GB', prefix: '+44'  },
  { name: 'Israel',               iso: 'IL', prefix: '+972' },
  { name: 'Mexico',               iso: 'MX', prefix: '+52'  },
  { name: 'Argentina',            iso: 'AR', prefix: '+54'  },
  { name: 'Australia',            iso: 'AU', prefix: '+61'  },
  { name: 'Austria',              iso: 'AT', prefix: '+43'  },
  { name: 'Belgium',              iso: 'BE', prefix: '+32'  },
  { name: 'Brazil',               iso: 'BR', prefix: '+55'  },
  { name: 'Chile',                iso: 'CL', prefix: '+56'  },
  { name: 'China',                iso: 'CN', prefix: '+86'  },
  { name: 'Colombia',             iso: 'CO', prefix: '+57'  },
  { name: 'Czech Republic',       iso: 'CZ', prefix: '+420' },
  { name: 'Denmark',              iso: 'DK', prefix: '+45'  },
  { name: 'Egypt',                iso: 'EG', prefix: '+20'  },
  { name: 'France',               iso: 'FR', prefix: '+33'  },
  { name: 'Germany',              iso: 'DE', prefix: '+49'  },
  { name: 'Greece',               iso: 'GR', prefix: '+30'  },
  { name: 'Hong Kong',            iso: 'HK', prefix: '+852' },
  { name: 'Hungary',              iso: 'HU', prefix: '+36'  },
  { name: 'India',                iso: 'IN', prefix: '+91'  },
  { name: 'Ireland',              iso: 'IE', prefix: '+353' },
  { name: 'Italy',                iso: 'IT', prefix: '+39'  },
  { name: 'Japan',                iso: 'JP', prefix: '+81'  },
  { name: 'Netherlands',          iso: 'NL', prefix: '+31'  },
  { name: 'New Zealand',          iso: 'NZ', prefix: '+64'  },
  { name: 'Norway',               iso: 'NO', prefix: '+47'  },
  { name: 'Poland',               iso: 'PL', prefix: '+48'  },
  { name: 'Portugal',             iso: 'PT', prefix: '+351' },
  { name: 'Romania',              iso: 'RO', prefix: '+40'  },
  { name: 'Russia',               iso: 'RU', prefix: '+7'   },
  { name: 'Saudi Arabia',         iso: 'SA', prefix: '+966' },
  { name: 'Singapore',            iso: 'SG', prefix: '+65'  },
  { name: 'South Africa',         iso: 'ZA', prefix: '+27'  },
  { name: 'South Korea',          iso: 'KR', prefix: '+82'  },
  { name: 'Spain',                iso: 'ES', prefix: '+34'  },
  { name: 'Sweden',               iso: 'SE', prefix: '+46'  },
  { name: 'Switzerland',          iso: 'CH', prefix: '+41'  },
  { name: 'Turkey',               iso: 'TR', prefix: '+90'  },
  { name: 'Ukraine',              iso: 'UA', prefix: '+380' },
  { name: 'United Arab Emirates', iso: 'AE', prefix: '+971' },
];

const KNOWN_PREFIXES = new Set(COUNTRY_CODES.map((c) => c.prefix));

/**
 * Split a stored '+…' value into { prefix, national } for display.
 * Longest match against the country table wins; unknown codes fall back
 * to the standard 1-digit (+1 / +7) then 2-digit split. Re-joining
 * `prefix + national` always reproduces the original digits.
 */
export function splitE164(value: string): { prefix: string; national: string } {
  const digits = value.replace(/[^\d]/g, '');
  for (const len of [3, 2, 1]) {
    const candidate = `+${digits.slice(0, len)}`;
    if (digits.length > len && KNOWN_PREFIXES.has(candidate)) {
      return { prefix: candidate, national: digits.slice(len) };
    }
  }
  const fallbackLen = digits.startsWith('1') || digits.startsWith('7') ? 1 : 2;
  return {
    prefix: `+${digits.slice(0, fallbackLen)}`,
    national: digits.slice(fallbackLen),
  };
}

// ── Resolved-default hook ────────────────────────────────────────────────────

/**
 * The tenant's effective default dialing prefix. Cached per scope; the
 * value changes rarely (it's an account setting), so a 5-minute staleTime
 * keeps dialogs snappy without going stale within a session.
 */
export function usePhoneDefaults(opts: {
  customerId?: string | null;
  resellerId?: string | null;
} = {}) {
  return useQuery({
    queryKey: ['phoneDefaults', opts.customerId ?? null, opts.resellerId ?? null],
    queryFn: () => phoneDefaults.get(opts),
    staleTime: 5 * 60_000,
  });
}

// ── Country list (shared between PhoneInput + CountryPrefixSelect) ──────────

function CountryCommandList({
  selectedPrefix, onSelect, extraItem,
}: {
  selectedPrefix: string | null;
  onSelect: (c: CountryCode | null) => void;
  /** Optional leading item (e.g. "Inherit platform default"). */
  extraItem?: { label: string; selected: boolean } | null;
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRY_CODES;
    return COUNTRY_CODES.filter((c) =>
      c.name.toLowerCase().includes(q)
      || c.iso.toLowerCase().includes(q)
      || c.prefix.includes(q.startsWith('+') ? q : `+${q.replace(/^\+/, '')}`)
      || c.prefix.slice(1).startsWith(q),
    );
  }, [search]);

  const common = filtered.filter((c) => COMMON_ISO.has(c.iso));
  const rest = filtered.filter((c) => !COMMON_ISO.has(c.iso));

  const renderItem = (c: CountryCode) => (
    <CommandItem
      key={c.iso}
      value={c.iso}
      onSelect={() => { onSelect(c); setSearch(''); }}
    >
      <Check
        className={cn(
          'me-2 h-4 w-4 shrink-0',
          selectedPrefix === c.prefix ? 'opacity-100' : 'opacity-0',
        )}
      />
      <span className="me-2" aria-hidden>{flagOf(c.iso)}</span>
      <span className="flex-1 truncate">{c.name}</span>
      <span className="ms-3 font-mono text-xs text-muted-foreground" dir="ltr">{c.prefix}</span>
    </CommandItem>
  );

  return (
    <Command shouldFilter={false}>
      <CommandInput
        placeholder="Search country or code…"
        value={search}
        onValueChange={setSearch}
      />
      <CommandList className="max-h-64">
        {filtered.length === 0 && !extraItem && <CommandEmpty>No country found.</CommandEmpty>}
        {extraItem && (
          <CommandGroup>
            <CommandItem value="__inherit__" onSelect={() => { onSelect(null); setSearch(''); }}>
              <Check
                className={cn('me-2 h-4 w-4 shrink-0', extraItem.selected ? 'opacity-100' : 'opacity-0')}
              />
              <span className="flex-1 truncate text-muted-foreground">{extraItem.label}</span>
            </CommandItem>
          </CommandGroup>
        )}
        {common.length > 0 && (
          <CommandGroup heading="Common">{common.map(renderItem)}</CommandGroup>
        )}
        {rest.length > 0 && (
          <CommandGroup heading={common.length > 0 ? 'All countries' : undefined}>
            {rest.map(renderItem)}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  );
}

// ── PhoneInput ───────────────────────────────────────────────────────────────

export interface PhoneInputProps {
  /** Full stored value ('' or '+<digits>'; legacy free-text tolerated). */
  value: string;
  /** Always receives the COMBINED value: '' or `${prefix}${nationalDigits}`. */
  onChange: (full: string) => void;
  /** Scope for the resolved default prefix (entered customer / detail page). */
  customerId?: string | null;
  resellerId?: string | null;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Wrapper classes (widths, margins). */
  className?: string;
  /** Classes for the national-number <Input> — size variants (h-8/h-9 text-sm)
   *  pass through here; the prefix button stretches to match. */
  inputClassName?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

export function PhoneInput({
  value,
  onChange,
  customerId,
  resellerId,
  id,
  placeholder = '555 123 4567',
  disabled = false,
  autoFocus = false,
  className,
  inputClassName,
  ...aria
}: PhoneInputProps) {
  const [open, setOpen] = useState(false);
  // Remembers a manually-picked prefix while the national box is empty
  // (an empty combined value can't carry the prefix choice).
  const [pickedPrefix, setPickedPrefix] = useState<string | null>(null);

  const { data } = usePhoneDefaults({ customerId, resellerId });
  const resolvedDefault = data?.prefix ?? '+1';

  const hasPlus = value.startsWith('+');
  const parsed = hasPlus ? splitE164(value) : null;
  const prefix = parsed?.prefix ?? pickedPrefix ?? resolvedDefault;
  const national = parsed ? parsed.national : value;

  const selectedCountry = COUNTRY_CODES.find((c) => c.prefix === prefix);

  function emit(nextPrefix: string, rawNational: string) {
    let digits = rawNational.replace(/\D/g, '');
    if (digits === '') { onChange(''); return; }
    // Mirror backend/src/utils/phone.ts applyDefaultPrefix():
    if (nextPrefix === '+1' && digits.length === 11 && digits.startsWith('1')) {
      onChange(`+${digits}`);
      return;
    }
    if (digits.startsWith('0')) digits = digits.slice(1);
    onChange(digits === '' ? '' : `${nextPrefix}${digits}`);
  }

  function handleNationalChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    if (raw.includes('+')) {
      // Full international paste — let the '+' win and re-derive the prefix.
      const digits = raw.replace(/\D/g, '');
      onChange(digits === '' ? '' : `+${digits}`);
      return;
    }
    emit(prefix, raw);
  }

  function handleCountrySelect(c: CountryCode | null) {
    if (!c) return; // no inherit item in this control
    setPickedPrefix(c.prefix);
    setOpen(false);
    if (national) emit(c.prefix, national);
  }

  return (
    <div className={cn('flex w-full min-w-0', className)} dir="ltr">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={`Country code: ${selectedCountry ? selectedCountry.name : prefix}`}
            disabled={disabled}
            className="h-auto min-w-0 shrink-0 self-stretch gap-1 rounded-e-none border-e-0 px-2.5 font-mono text-sm font-normal text-foreground"
          >
            {selectedCountry && <span aria-hidden className="text-base leading-none">{flagOf(selectedCountry.iso)}</span>}
            <span dir="ltr">{prefix}</span>
            <ChevronDown className="!h-3 !w-3 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <CountryCommandList selectedPrefix={prefix} onSelect={handleCountrySelect} />
        </PopoverContent>
      </Popover>
      <Input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        value={national}
        onChange={handleNationalChange}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        dir="ltr"
        className={cn('min-w-0 flex-1 rounded-s-none font-mono', inputClassName)}
        {...aria}
      />
    </div>
  );
}

// ── CountryPrefixSelect ──────────────────────────────────────────────────────

/**
 * Standalone country-code picker for SETTINGS surfaces (reseller / customer
 * detail, platform settings) — picks a prefix (or "inherit" when
 * `inheritLabel` is provided), without a national-number box.
 */
export function CountryPrefixSelect({
  value,
  onChange,
  inheritLabel,
  disabled = false,
  className,
  id,
}: {
  /** Selected prefix ('+1') or null (= inherit, when inheritLabel is set). */
  value: string | null;
  onChange: (prefix: string | null) => void;
  /** When set, a leading "inherit" item is offered and null is selectable. */
  inheritLabel?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = value ? COUNTRY_CODES.find((c) => c.prefix === value) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between font-normal', className)}
        >
          <span className="flex min-w-0 items-center gap-2">
            {value ? (
              <>
                {selected && <span aria-hidden>{flagOf(selected.iso)}</span>}
                <span className="truncate">{selected ? selected.name : 'Custom'}</span>
                <span className="font-mono text-xs text-muted-foreground" dir="ltr">{value}</span>
              </>
            ) : (
              <span className="truncate text-muted-foreground">{inheritLabel ?? 'Select country…'}</span>
            )}
          </span>
          <ChevronDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <CountryCommandList
          selectedPrefix={value}
          onSelect={(c) => { onChange(c ? c.prefix : null); setOpen(false); }}
          extraItem={inheritLabel ? { label: inheritLabel, selected: value === null } : null}
        />
      </PopoverContent>
    </Popover>
  );
}
